import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  FileText,
  Download,
  Package,
  AlertTriangle,
  Wrench,
  MapPin,
  FolderTree,
  History,
  FileSpreadsheet,
  Truck,
  ShoppingCart,
  TrendingUp,
  DollarSign,
  ArrowLeftRight,
  RefreshCw,
  Archive,
  Zap,
  Users,
  BarChart3,
  PieChart,
  Calendar,
  Filter,
  LayoutDashboard
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Chart colors
const CHART_COLORS = ['#0f172a', '#0284c7', '#059669', '#d97706', '#dc2626', '#7c3aed', '#db2777', '#64748b'];

// Report definitions organized by category
const reportCategories = {
  inventory: {
    title: 'Inventory Reports',
    icon: Package,
    chartEndpoint: '/reports/charts/inventory-overview',
    reports: [
      { id: 'inventory', name: 'Full Inventory Report', description: 'Complete list of all parts with quantities, prices, and values', icon: Package, endpoint: '/reports/inventory', filters: ['category', 'location', 'supplier', 'dateRange'] },
      { id: 'low-stock', name: 'Low Stock Report', description: 'Parts below minimum quantity with reorder costs', icon: AlertTriangle, endpoint: '/reports/low-stock', filters: ['category', 'location'] },
      { id: 'tools', name: 'Tools Report', description: 'All tools with status and location', icon: Wrench, endpoint: '/reports/tools', filters: ['location', 'tool_status'] },
      { id: 'location-stock', name: 'Stock by Location', description: 'Stock summary grouped by warehouse/location', icon: MapPin, endpoint: '/reports/location-stock', filters: [] },
      { id: 'category-stock', name: 'Stock by Category', description: 'Stock summary grouped by part category', icon: FolderTree, endpoint: '/reports/category-stock', filters: [] }
    ]
  },
  financial: {
    title: 'Financial Reports',
    icon: DollarSign,
    chartEndpoint: '/reports/charts/financial-overview',
    reports: [
      { id: 'profit-margin', name: 'Profit Margin Report', description: 'Parts ranked by profit margin from high to low', icon: TrendingUp, endpoint: '/reports/profit-margin', filters: ['category', 'location', 'marginRange'] },
      { id: 'stock-valuation', name: 'Stock Valuation Report', description: 'Total inventory value grouped by category/location/supplier', icon: DollarSign, endpoint: '/reports/stock-valuation', filters: ['groupBy'] },
      { id: 'customer-profitability', name: 'Customer Profitability', description: 'Revenue and profit breakdown per customer', icon: Users, endpoint: '/reports/customer-profitability', filters: ['dateRange'] }
    ]
  },
  operational: {
    title: 'Operational Reports',
    icon: ArrowLeftRight,
    chartEndpoint: '/reports/charts/operations-overview',
    reports: [
      { id: 'stock-movement', name: 'Stock Movement Report', description: 'Transfers between locations with dates and quantities', icon: ArrowLeftRight, endpoint: '/reports/stock-movement', filters: ['location', 'dateRange'] },
      { id: 'reorder-suggestions', name: 'Reorder Suggestions', description: 'Parts needing restocking with estimated costs', icon: RefreshCw, endpoint: '/reports/reorder-suggestions', filters: ['category', 'supplier'] },
      { id: 'dead-stock', name: 'Dead Stock Report', description: 'Parts with no movement in specified period', icon: Archive, endpoint: '/reports/dead-stock', filters: ['category', 'daysInactive'] },
      { id: 'fast-moving', name: 'Fast-Moving Parts', description: 'Most frequently issued parts in recent period', icon: Zap, endpoint: '/reports/fast-moving', filters: ['category', 'days', 'limit'] }
    ]
  },
  customer: {
    title: 'Customer & Job Reports',
    icon: Users,
    reports: [
      { id: 'customer-history', name: 'Customer Job History', description: 'Parts issued per customer with costs and revenue', icon: History, endpoint: '/reports/customer-history', filters: ['customer', 'dateRange'] },
      { id: 'parts-usage', name: 'Parts Usage Report', description: 'Which parts are used most in customer jobs', icon: BarChart3, endpoint: '/reports/parts-usage', filters: ['category', 'dateRange'] }
    ]
  },
  supplier: {
    title: 'Supplier & PO Reports',
    icon: Truck,
    chartEndpoint: '/reports/charts/supplier-overview',
    reports: [
      { id: 'supplier', name: 'Supplier Report', description: 'Suppliers with parts count and stock value', icon: Truck, endpoint: '/reports/supplier', filters: ['supplier'] },
      { id: 'supplier-comparison', name: 'Supplier Comparison', description: 'Compare pricing and stock across suppliers', icon: PieChart, endpoint: '/reports/supplier-comparison', filters: ['category'] },
      { id: 'purchase-orders', name: 'Purchase Orders Report', description: 'All POs with status, amounts, and delivery dates', icon: ShoppingCart, endpoint: '/reports/purchase-orders', filters: ['supplier', 'po_status', 'dateRange'] },
      { id: 'po-fulfillment', name: 'PO Fulfillment Report', description: 'Delivery times and on-time performance by supplier', icon: Calendar, endpoint: '/reports/po-fulfillment', filters: ['supplier', 'dateRange'] }
    ]
  },
  system: {
    title: 'System Reports',
    icon: History,
    reports: [
      { id: 'audit-log', name: 'Audit Log Report', description: 'History of all system changes and actions', icon: History, endpoint: '/reports/audit-log', filters: ['entity_type', 'dateRange'] },
      { id: 'monthly-summary', name: 'Monthly Summary', description: 'Complete activity snapshot for a month', icon: Calendar, endpoint: '/reports/monthly-summary', filters: ['yearMonth'] }
    ]
  }
};

// Chart Dashboard Component
const ChartDashboard = ({ category, chartData, loading }) => {
  const { formatCurrency } = useSettings();
  
  if (loading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 mb-6">
        {[1, 2, 3, 4].map(i => (
          <Card key={i} className="border-slate-200 animate-pulse">
            <CardContent className="p-6 h-64 bg-slate-100 rounded-sm" />
          </Card>
        ))}
      </div>
    );
  }
  
  if (!chartData) return null;
  
  const renderChart = (data, type, title) => {
    if (!data || !data.labels || data.labels.length === 0) return null;
    if (!data.values || data.values.length === 0) return null;
    
    const chartDataFormatted = data.labels.map((label, i) => ({
      name: label && label.length > 15 ? label.substring(0, 15) + '...' : (label || 'Unknown'),
      value: data.values[i] || 0,
      fullName: label || 'Unknown'
    }));
    
    if (type === 'pie' || type === 'donut') {
      return (
        <Card className="border-slate-200 shadow-sm" data-testid={`chart-${title.replace(/\s+/g, '-').toLowerCase()}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">{title}</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <RechartsPieChart>
                <Pie
                  data={chartDataFormatted}
                  cx="50%"
                  cy="50%"
                  innerRadius={type === 'donut' ? 50 : 0}
                  outerRadius={80}
                  paddingAngle={2}
                  dataKey="value"
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {chartDataFormatted.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip formatter={(value) => value.toLocaleString()} />
              </RechartsPieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      );
    }
    
    if (type === 'bar' || type === 'bar_horizontal') {
      return (
        <Card className="border-slate-200 shadow-sm" data-testid={`chart-${title.replace(/\s+/g, '-').toLowerCase()}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">{title}</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartDataFormatted} layout={type === 'bar_horizontal' ? 'vertical' : 'horizontal'}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                {type === 'bar_horizontal' ? (
                  <>
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={80} />
                  </>
                ) : (
                  <>
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-45} textAnchor="end" height={60} />
                    <YAxis tick={{ fontSize: 10 }} />
                  </>
                )}
                <Tooltip formatter={(value) => typeof value === 'number' ? value.toLocaleString() : value} />
                <Bar dataKey="value" fill={CHART_COLORS[0]} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      );
    }
    
    if (type === 'line' || type === 'area') {
      return (
        <Card className="border-slate-200 shadow-sm" data-testid={`chart-${title.replace(/\s+/g, '-').toLowerCase()}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">{title}</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              {type === 'area' ? (
                <AreaChart data={chartDataFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Area type="monotone" dataKey="value" stroke={CHART_COLORS[1]} fill={CHART_COLORS[1]} fillOpacity={0.3} />
                </AreaChart>
              ) : (
                <LineChart data={chartDataFormatted}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 10 }} />
                  <Tooltip />
                  <Line type="monotone" dataKey="value" stroke={CHART_COLORS[1]} strokeWidth={2} dot={{ fill: CHART_COLORS[1] }} />
                </LineChart>
              )}
            </ResponsiveContainer>
          </CardContent>
        </Card>
      );
    }
    
    if (type === 'grouped_bar' && data.datasets) {
      const formattedData = data.labels.map((label, i) => {
        const entry = { name: label && label.length > 12 ? label.substring(0, 12) + '...' : (label || 'Unknown') };
        data.datasets.forEach(ds => {
          entry[ds.label] = ds.values && ds.values[i] !== undefined ? ds.values[i] : 0;
        });
        return entry;
      });
      
      return (
        <Card className="border-slate-200 shadow-sm col-span-2" data-testid={`chart-${title.replace(/\s+/g, '-').toLowerCase()}`}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">{title}</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={formattedData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                <YAxis tick={{ fontSize: 10 }} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Legend />
                {data.datasets.map((ds, i) => (
                  <Bar key={ds.label} dataKey={ds.label} fill={ds.color || CHART_COLORS[i]} radius={[4, 4, 0, 0]} />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      );
    }
    
    return null;
  };
  
  // Render summary stats
  const renderSummaryStats = () => {
    const stats = [];
    if (chartData.total_parts !== undefined) stats.push({ label: 'Total Parts', value: chartData.total_parts });
    if (chartData.low_stock_count !== undefined) stats.push({ label: 'Low Stock', value: chartData.low_stock_count, danger: chartData.low_stock_count > 0 });
    if (chartData.total_value !== undefined) stats.push({ label: 'Total Value', value: formatCurrency(chartData.total_value) });
    if (chartData.total_revenue !== undefined) stats.push({ label: 'Revenue Potential', value: formatCurrency(chartData.total_revenue) });
    if (chartData.total_cost !== undefined) stats.push({ label: 'Total Cost', value: formatCurrency(chartData.total_cost) });
    if (chartData.avg_margin !== undefined) stats.push({ label: 'Avg Margin', value: `${chartData.avg_margin.toFixed(1)}%` });
    if (chartData.total_tools !== undefined) stats.push({ label: 'Total Tools', value: chartData.total_tools });
    if (chartData.total_orders !== undefined) stats.push({ label: 'Total POs', value: chartData.total_orders });
    if (chartData.total_suppliers !== undefined) stats.push({ label: 'Suppliers', value: chartData.total_suppliers });
    
    if (stats.length === 0) return null;
    
    return (
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        {stats.map((stat, i) => (
          <Card key={i} className={`border-slate-200 ${stat.danger ? 'border-red-500 bg-red-50' : ''}`}>
            <CardContent className="p-3">
              <p className="text-xs text-slate-500 uppercase">{stat.label}</p>
              <p className={`text-lg font-bold ${stat.danger ? 'text-red-600' : 'text-slate-900'}`}>{stat.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };
  
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-4">
        <LayoutDashboard className="w-5 h-5 text-slate-600" />
        <h3 className="text-lg font-bold text-slate-800">Visual Dashboard</h3>
      </div>
      
      {renderSummaryStats()}
      
      <div className="grid gap-4 md:grid-cols-2">
        {chartData.parts_by_category && renderChart(chartData.parts_by_category, chartData.parts_by_category.type, 'Parts by Category')}
        {chartData.parts_by_location && renderChart(chartData.parts_by_location, chartData.parts_by_location.type, 'Parts by Location')}
        {chartData.stock_status && renderChart(chartData.stock_status, chartData.stock_status.type, 'Stock Status')}
        {chartData.value_by_category && renderChart(chartData.value_by_category, chartData.value_by_category.type, 'Value by Category')}
        {chartData.margin_distribution && renderChart(chartData.margin_distribution, chartData.margin_distribution.type, 'Profit Margin Distribution')}
        {chartData.cost_vs_revenue && renderChart(chartData.cost_vs_revenue, chartData.cost_vs_revenue.type, 'Cost vs Revenue by Category')}
        {chartData.top_valuable_parts && renderChart(chartData.top_valuable_parts, chartData.top_valuable_parts.type, 'Top 10 Most Valuable Parts')}
        {chartData.tools_by_status && renderChart(chartData.tools_by_status, chartData.tools_by_status.type, 'Tools by Status')}
        {chartData.po_by_status && renderChart(chartData.po_by_status, chartData.po_by_status.type, 'Purchase Orders by Status')}
        {chartData.monthly_issuances && renderChart(chartData.monthly_issuances, chartData.monthly_issuances.type, 'Monthly Issuances')}
        {chartData.monthly_transfers && renderChart(chartData.monthly_transfers, chartData.monthly_transfers.type, 'Monthly Transfers')}
        {chartData.parts_by_supplier && renderChart(chartData.parts_by_supplier, chartData.parts_by_supplier.type, 'Parts by Supplier')}
        {chartData.value_by_supplier && renderChart(chartData.value_by_supplier, chartData.value_by_supplier.type, 'Value by Supplier')}
      </div>
    </div>
  );
};

const Reports = () => {
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState({});
  const [selectedFilters, setSelectedFilters] = useState({});
  const [activeTab, setActiveTab] = useState('inventory');
  const [chartData, setChartData] = useState({});
  const [chartLoading, setChartLoading] = useState({});
  const { canViewPrices } = useAuth();

  useEffect(() => {
    fetchFilterData();
  }, []);

  useEffect(() => {
    fetchChartData(activeTab);
  }, [activeTab]);

  const fetchFilterData = async () => {
    try {
      const [categoriesRes, locationsRes, suppliersRes, customersRes] = await Promise.all([
        axios.get(`${API}/categories`),
        axios.get(`${API}/locations`),
        axios.get(`${API}/suppliers`),
        axios.get(`${API}/customers`)
      ]);
      setCategories(categoriesRes.data);
      setLocations(locationsRes.data);
      setSuppliers(suppliersRes.data);
      setCustomers(customersRes.data);
    } catch (error) {
      console.error('Failed to load filter data');
    }
  };

  const fetchChartData = async (tab) => {
    const category = reportCategories[tab];
    if (!category?.chartEndpoint) return;
    
    setChartLoading({ ...chartLoading, [tab]: true });
    try {
      const response = await axios.get(`${API}${category.chartEndpoint}`);
      setChartData({ ...chartData, [tab]: response.data });
    } catch (error) {
      console.error('Failed to load chart data:', error);
    } finally {
      setChartLoading({ ...chartLoading, [tab]: false });
    }
  };

  const handleDownload = async (report, format) => {
    setLoading({ ...loading, [`${report.id}-${format}`]: true });
    
    try {
      let url = `${API}${report.endpoint}?format=${format}`;
      
      const filters = selectedFilters[report.id] || {};
      Object.entries(filters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          url += `&${key}=${encodeURIComponent(value)}`;
        }
      });
      
      const response = await axios.get(url, { responseType: 'blob' });
      
      const blob = new Blob([response.data]);
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${report.id}_report.${format}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(downloadUrl);
      
      toast.success(`${report.name} downloaded successfully!`);
    } catch (error) {
      toast.error('Failed to generate report');
      console.error(error);
    } finally {
      setLoading({ ...loading, [`${report.id}-${format}`]: false });
    }
  };

  const updateFilter = (reportId, filterName, value) => {
    setSelectedFilters({
      ...selectedFilters,
      [reportId]: {
        ...(selectedFilters[reportId] || {}),
        [filterName]: value === 'all' ? undefined : value
      }
    });
  };

  const renderFilters = (report) => {
    const filters = selectedFilters[report.id] || {};
    
    return (
      <div className="space-y-2">
        {report.filters.includes('category') && (
          <Select value={filters.category_id || 'all'} onValueChange={(value) => updateFilter(report.id, 'category_id', value)}>
            <SelectTrigger className="rounded-sm text-sm h-9"><SelectValue placeholder="All Categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(cat => (<SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>))}
            </SelectContent>
          </Select>
        )}
        
        {report.filters.includes('location') && (
          <Select value={filters.location_id || 'all'} onValueChange={(value) => updateFilter(report.id, 'location_id', value)}>
            <SelectTrigger className="rounded-sm text-sm h-9"><SelectValue placeholder="All Locations" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Locations</SelectItem>
              {locations.map(loc => (<SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>))}
            </SelectContent>
          </Select>
        )}
        
        {report.filters.includes('supplier') && (
          <Select value={filters.supplier_id || 'all'} onValueChange={(value) => updateFilter(report.id, 'supplier_id', value)}>
            <SelectTrigger className="rounded-sm text-sm h-9"><SelectValue placeholder="All Suppliers" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Suppliers</SelectItem>
              {suppliers.map(sup => (<SelectItem key={sup.id} value={sup.id}>{sup.name}</SelectItem>))}
            </SelectContent>
          </Select>
        )}
        
        {report.filters.includes('customer') && (
          <Select value={filters.customer_id || 'all'} onValueChange={(value) => updateFilter(report.id, 'customer_id', value)}>
            <SelectTrigger className="rounded-sm text-sm h-9"><SelectValue placeholder="All Customers" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Customers</SelectItem>
              {customers.map(cust => (<SelectItem key={cust.id} value={cust.id}>{cust.name}</SelectItem>))}
            </SelectContent>
          </Select>
        )}
        
        {report.filters.includes('entity_type') && (
          <Select value={filters.entity_type || 'all'} onValueChange={(value) => updateFilter(report.id, 'entity_type', value)}>
            <SelectTrigger className="rounded-sm text-sm h-9"><SelectValue placeholder="All Types" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="part">Parts</SelectItem>
              <SelectItem value="tool">Tools</SelectItem>
              <SelectItem value="category">Categories</SelectItem>
              <SelectItem value="location">Locations</SelectItem>
              <SelectItem value="supplier">Suppliers</SelectItem>
              <SelectItem value="user">Users</SelectItem>
              <SelectItem value="purchase_order">Purchase Orders</SelectItem>
              <SelectItem value="stock_issuance">Stock Issuances</SelectItem>
              <SelectItem value="customer">Customers</SelectItem>
            </SelectContent>
          </Select>
        )}
        
        {report.filters.includes('po_status') && (
          <Select value={filters.status || 'all'} onValueChange={(value) => updateFilter(report.id, 'status', value)}>
            <SelectTrigger className="rounded-sm text-sm h-9"><SelectValue placeholder="All Statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="draft">Draft</SelectItem>
              <SelectItem value="requested">Requested</SelectItem>
              <SelectItem value="approved">Approved</SelectItem>
              <SelectItem value="ordered">Ordered</SelectItem>
              <SelectItem value="shipped">Shipped</SelectItem>
              <SelectItem value="received">Received</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        )}
        
        {report.filters.includes('tool_status') && (
          <Select value={filters.status || 'all'} onValueChange={(value) => updateFilter(report.id, 'status', value)}>
            <SelectTrigger className="rounded-sm text-sm h-9"><SelectValue placeholder="All Statuses" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="available">Available</SelectItem>
              <SelectItem value="in_use">In Use</SelectItem>
              <SelectItem value="maintenance">Maintenance</SelectItem>
              <SelectItem value="retired">Retired</SelectItem>
            </SelectContent>
          </Select>
        )}
        
        {report.filters.includes('groupBy') && (
          <Select value={filters.group_by || 'category'} onValueChange={(value) => updateFilter(report.id, 'group_by', value)}>
            <SelectTrigger className="rounded-sm text-sm h-9"><SelectValue placeholder="Group By" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="category">By Category</SelectItem>
              <SelectItem value="location">By Location</SelectItem>
              <SelectItem value="supplier">By Supplier</SelectItem>
            </SelectContent>
          </Select>
        )}
        
        {report.filters.includes('daysInactive') && (
          <Select value={filters.days_inactive || '90'} onValueChange={(value) => updateFilter(report.id, 'days_inactive', value)}>
            <SelectTrigger className="rounded-sm text-sm h-9"><SelectValue placeholder="Inactive Period" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="30">30 Days</SelectItem>
              <SelectItem value="60">60 Days</SelectItem>
              <SelectItem value="90">90 Days</SelectItem>
              <SelectItem value="180">180 Days</SelectItem>
              <SelectItem value="365">1 Year</SelectItem>
            </SelectContent>
          </Select>
        )}
        
        {report.filters.includes('days') && (
          <Select value={filters.days || '30'} onValueChange={(value) => updateFilter(report.id, 'days', value)}>
            <SelectTrigger className="rounded-sm text-sm h-9"><SelectValue placeholder="Period" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 Days</SelectItem>
              <SelectItem value="14">Last 14 Days</SelectItem>
              <SelectItem value="30">Last 30 Days</SelectItem>
              <SelectItem value="60">Last 60 Days</SelectItem>
              <SelectItem value="90">Last 90 Days</SelectItem>
            </SelectContent>
          </Select>
        )}
        
        {report.filters.includes('limit') && (
          <Select value={filters.limit || '50'} onValueChange={(value) => updateFilter(report.id, 'limit', value)}>
            <SelectTrigger className="rounded-sm text-sm h-9"><SelectValue placeholder="Top N Parts" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="10">Top 10</SelectItem>
              <SelectItem value="25">Top 25</SelectItem>
              <SelectItem value="50">Top 50</SelectItem>
              <SelectItem value="100">Top 100</SelectItem>
            </SelectContent>
          </Select>
        )}
        
        {report.filters.includes('yearMonth') && (
          <div className="flex gap-2">
            <Input type="number" placeholder="Year" min="2020" max="2030" value={filters.year || new Date().getFullYear()} onChange={(e) => updateFilter(report.id, 'year', e.target.value)} className="rounded-sm h-9 w-20" />
            <Select value={filters.month || String(new Date().getMonth() + 1)} onValueChange={(value) => updateFilter(report.id, 'month', value)}>
              <SelectTrigger className="rounded-sm text-sm h-9 flex-1"><SelectValue placeholder="Month" /></SelectTrigger>
              <SelectContent>
                {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, i) => (
                  <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        
        {report.filters.includes('dateRange') && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-500 w-12">From</Label>
              <Input type="date" value={filters.start_date || ''} onChange={(e) => updateFilter(report.id, 'start_date', e.target.value)} className="rounded-sm h-9 text-sm" />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-xs text-slate-500 w-12">To</Label>
              <Input type="date" value={filters.end_date || ''} onChange={(e) => updateFilter(report.id, 'end_date', e.target.value)} className="rounded-sm h-9 text-sm" />
            </div>
          </div>
        )}
        
        {report.filters.includes('marginRange') && (
          <div className="flex gap-2">
            <Input type="number" placeholder="Min %" value={filters.min_margin || ''} onChange={(e) => updateFilter(report.id, 'min_margin', e.target.value)} className="rounded-sm h-9 w-20 text-sm" />
            <Input type="number" placeholder="Max %" value={filters.max_margin || ''} onChange={(e) => updateFilter(report.id, 'max_margin', e.target.value)} className="rounded-sm h-9 w-20 text-sm" />
          </div>
        )}
      </div>
    );
  };

  const renderReportCard = (report) => {
    const Icon = report.icon;
    const isFinancialReport = ['profit-margin', 'stock-valuation', 'customer-profitability'].includes(report.id);
    if (isFinancialReport && !canViewPrices()) return null;
    
    return (
      <Card key={report.id} className="border-slate-200 shadow-sm rounded-sm card-hover" data-testid={`report-card-${report.id}`}>
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="w-10 h-10 bg-slate-100 rounded-sm flex items-center justify-center">
              <Icon className="w-5 h-5 text-slate-600" />
            </div>
            <Badge variant="secondary" className="rounded-sm text-xs">
              <Filter className="w-3 h-3 mr-1" />
              {report.filters.length} filters
            </Badge>
          </div>
          <CardTitle className="text-lg font-bold mt-3">{report.name}</CardTitle>
          <CardDescription className="text-xs">{report.description}</CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {report.filters.length > 0 && renderFilters(report)}
          
          <div className="grid grid-cols-4 gap-1">
            <Button variant="outline" size="sm" className="rounded-sm text-xs px-2" onClick={() => handleDownload(report, 'pdf')} disabled={loading[`${report.id}-pdf`]} data-testid={`download-pdf-${report.id}`}>
              {loading[`${report.id}-pdf`] ? '...' : 'PDF'}
            </Button>
            <Button variant="outline" size="sm" className="rounded-sm text-xs px-2" onClick={() => handleDownload(report, 'xlsx')} disabled={loading[`${report.id}-xlsx`]} data-testid={`download-xlsx-${report.id}`}>
              {loading[`${report.id}-xlsx`] ? '...' : 'Excel'}
            </Button>
            <Button variant="outline" size="sm" className="rounded-sm text-xs px-2" onClick={() => handleDownload(report, 'csv')} disabled={loading[`${report.id}-csv`]} data-testid={`download-csv-${report.id}`}>
              {loading[`${report.id}-csv`] ? '...' : 'CSV'}
            </Button>
            <Button variant="outline" size="sm" className="rounded-sm text-xs px-2" onClick={() => handleDownload(report, 'docx')} disabled={loading[`${report.id}-docx`]} data-testid={`download-docx-${report.id}`}>
              {loading[`${report.id}-docx`] ? '...' : 'Word'}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-black text-slate-900 tracking-tight">Reports & Analytics</h1>
          <p className="text-slate-500 mt-1 text-sm">Visual dashboards and exportable reports in PDF, Excel, CSV, or Word format</p>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant="secondary" className="rounded-sm">
            <BarChart3 className="w-3 h-3 mr-1" />
            Interactive Charts
          </Badge>
          <Badge variant="secondary" className="rounded-sm">
            <FileSpreadsheet className="w-3 h-3 mr-1" />
            {Object.values(reportCategories).reduce((acc, cat) => acc + cat.reports.length, 0)} Reports
          </Badge>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <div className="overflow-x-auto">
          <TabsList className="inline-flex h-auto p-1 bg-slate-100 rounded-sm">
            {Object.entries(reportCategories).map(([key, category]) => {
              const Icon = category.icon;
              return (
                <TabsTrigger key={key} value={key} className="rounded-sm px-3 py-2 text-xs sm:text-sm data-[state=active]:bg-white data-[state=active]:shadow-sm">
                  <Icon className="w-4 h-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">{category.title.replace(' Reports', '')}</span>
                  <span className="sm:hidden">{category.title.split(' ')[0]}</span>
                </TabsTrigger>
              );
            })}
          </TabsList>
        </div>

        {Object.entries(reportCategories).map(([key, category]) => (
          <TabsContent key={key} value={key} className="space-y-4">
            <div className="flex items-center gap-2 mb-4">
              {(() => { const Icon = category.icon; return <Icon className="w-5 h-5 text-slate-600" />; })()}
              <h2 className="text-lg font-bold text-slate-800">{category.title}</h2>
              <Badge variant="outline" className="rounded-sm text-xs ml-auto">{category.reports.length} reports</Badge>
            </div>
            
            {/* Chart Dashboard */}
            {category.chartEndpoint && (
              <ChartDashboard 
                category={key} 
                chartData={chartData[key]} 
                loading={chartLoading[key]} 
              />
            )}
            
            {/* Report Cards */}
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {category.reports.map(report => renderReportCard(report))}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
};

export default Reports;
